import { Shield, AlertTriangle, Flame, Skull } from 'lucide-react';

/**
 * Classifies leverage into 4 risk tiers and renders an appropriate badge + warning.
 * Shared across Futures page, Smart Trade Terminal, and any UI that exposes leverage.
 *
 *   1-10x:   Conservative (green)
 *   10-50x:  Aggressive (amber)
 *   50-125x: Extreme (red)
 *   125x+:   MEXC-Only (purple — only MEXC supports this; user assumes all risk)
 */

export const LEVERAGE_TIERS = {
  conservative: {
    id: 'conservative',
    label: 'Conservative',
    range: [1, 10],
    color: 'emerald',
    icon: Shield,
    description: 'Industry-standard leverage. Liquidation requires meaningful adverse moves.',
    warning: null,
  },
  aggressive: {
    id: 'aggressive',
    label: 'Aggressive',
    range: [10, 50],
    color: 'amber',
    icon: AlertTriangle,
    description: 'Above 10x, normal volatility can liquidate fast. Use tight stop-losses.',
    warning: 'Above 10x, a 10% adverse move can liquidate you.',
  },
  extreme: {
    id: 'extreme',
    label: 'Extreme',
    range: [50, 125],
    color: 'red',
    icon: Flame,
    description: 'Above 50x, >95% of users historically liquidated within minutes during normal volatility.',
    warning: 'EXTREME RISK — historical data shows >95% liquidation rate at this leverage.',
  },
  mexcOnly: {
    id: 'mexcOnly',
    label: 'MEXC-Only',
    range: [125, 500],
    color: 'violet',
    icon: Skull,
    description: 'Only MEXC supports leverage above 125x. haythix does not endorse this leverage tier.',
    warning: 'Only available on MEXC. You bear all risk — haythix is a routing platform only.',
  },
};

export function classifyLeverage(leverage) {
  const lev = Number(leverage);
  if (!Number.isFinite(lev) || lev < 1) return LEVERAGE_TIERS.conservative;
  if (lev <= 10) return LEVERAGE_TIERS.conservative;
  if (lev <= 50) return LEVERAGE_TIERS.aggressive;
  if (lev <= 125) return LEVERAGE_TIERS.extreme;
  return LEVERAGE_TIERS.mexcOnly;
}

const COLOR_CLASSES = {
  emerald: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400' },
  amber:   { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   text: 'text-amber-400' },
  red:     { bg: 'bg-red-500/10',     border: 'border-red-500/30',     text: 'text-red-400' },
  violet:  { bg: 'bg-violet-500/10',  border: 'border-violet-500/30',  text: 'text-violet-400' },
};

export default function LeverageTierBadge({ leverage, showWarning = false, compact = false }) {
  const tier = classifyLeverage(leverage);
  const Icon = tier.icon;
  const c = COLOR_CLASSES[tier.color];

  if (compact) {
    return (
      <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${c.bg} ${c.border} ${c.text}`}>
        <Icon className="w-3 h-3" /> {tier.label}
      </span>
    );
  }

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${c.bg} ${c.border}`}>
      <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${c.text}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-xs font-bold uppercase tracking-wider ${c.text}`}>{tier.label}</span>
          <span className="text-xs text-gray-500 font-mono">{leverage}x</span>
        </div>
        <p className="text-xs text-gray-400">{tier.description}</p>
        {showWarning && tier.warning && (
          <p className={`text-xs font-semibold mt-1.5 ${c.text}`}>⚠️ {tier.warning}</p>
        )}
      </div>
    </div>
  );
}
